import { Controller } from "@hotwired/stimulus";
import { parsePhoneNumberWithError, getCountries, getCountryCallingCode } from "libphonenumber-js";

const DISPLAY_NAMES = new Intl.DisplayNames(["en"], { type: "region" });

export default class extends Controller {
  static targets = ["countrySelect", "localNumber", "phone", "error"];

  // The submit button lives outside this controller's element, so we look it
  // up via the parent form rather than using a Stimulus target.
  get #submitButton() {
    return this.element.closest("form")?.querySelector("[type=submit]") ?? null;
  }

  connect() {
    this.populateCountries();
    this.restoreExistingValue();
  }

  populateCountries() {
    const select = this.countrySelectTarget;
  
    const all = getCountries()
      .map((code) => ({
        code,
        callingCode: getCountryCallingCode(code),
        name: DISPLAY_NAMES.of(code) ?? code,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    for (const { code, callingCode, name } of all) {
      const opt = document.createElement("option");
      opt.value = code;
      opt.textContent = `${name} (+${callingCode})`;
      select.appendChild(opt);
    }
  }

  // On server re-render after a validation failure, @access_application.phone
  // already has a value in the hidden field — restore it to the visible input
  // and immediately validate so errors show without waiting for blur.
  // On a plain browser refresh the browser restores the visible input but NOT
  // the hidden field, so we check both sources.
  restoreExistingValue() {
    // Default to Italy if no existing value
    this.countrySelectTarget.value = "IT";

    const hidden = this.phoneTarget.value.trim();
    if (hidden) {
      this.localNumberTarget.value = hidden;
    }

    if (this.localNumberTarget.value.trim()) {
      this.validate();
    }
  }

  // Combines the selected calling code with the local number and writes the
  // full E.164-style number into the hidden field that the form submits.
  combine() {
    const local = this.localNumberTarget.value.trim();

    if (!local) {
      this.phoneTarget.value = "";
      return;
    }

    // If the user pasted a full international number, use it as-is.
    if (local.startsWith("+")) {
      this.phoneTarget.value = local;
      return;
    }

    const callingCode = getCountryCallingCode(this.countrySelectTarget.value);
    // Strip a leading trunk prefix zero (common in many countries).
    const stripped = local.replace(/^0+/, "");
    this.phoneTarget.value = `+${callingCode}${stripped}`;
  }

  validate() {
    this.combine();

    const phone = this.phoneTarget.value.trim();

    if (!phone) {
      this.clearError();
      return;
    }

    if (this.#parseValid(phone)) {
      this.clearError();
    } else {
      this.showError("Phone number is not valid");
    }
  }

  #parseValid(phone) {
    try {
      return parsePhoneNumberWithError(phone).isValid();
    } catch {
      return false;
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = "";
      this.errorTarget.hidden = true;
    }
    this.localNumberTarget.classList.remove("border-red-500");
    this.#setSubmitDisabled(false);
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = false;
    }
    this.localNumberTarget.classList.add("border-red-500");
    this.#setSubmitDisabled(true);
  }

  #setSubmitDisabled(disabled) {
    const btn = this.#submitButton;
    if (!btn) return;
    btn.disabled = disabled;
    btn.classList.toggle("opacity-50", disabled);
    btn.classList.toggle("cursor-not-allowed", disabled);
  }
}
